import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { ReportTemplateDetail, ReportTemplateList } from '@futrli-sdk/predict';

import { fetchReportTemplateById } from '../core/api/reporting/report-templates';
import { useReportsContext } from './reports.context';
import { OrientationType, PageSetup, ReportMappingData } from './types';
import { trackCreateReport } from './tracking';
import { useI18nConstants } from './use-i18n-constants';
import { usePredictFlags } from '../hooks/usePredictFlags';

export const useReportTemplates = () => {
  const intl = useIntl();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId as string;
  const organisationId = tempOrganisationId as string;
  const { createReport, reports } = useReportsContext();
  const { getHighestUntitledReportNum } = useI18nConstants();
  const { hasSwitchLocaleEnabled } = usePredictFlags();

  /**
   * Generates a name that can be used for a new report based upon the given parameters
   */
  const getNewReportName = useCallback(
    (orientation: OrientationType, sourceTemplate?: ReportTemplateList, isOnboarding?: boolean) => {
      const reportLN = intl.formatMessage({ id: 'reporting.report', defaultMessage: 'Report' });
      const workspaceTemplateLN = intl.formatMessage({
        id: 'reporting.workspace_template',
        defaultMessage: 'Workspace template'
      });
      let name = '';
      if (isOnboarding) {
        name =
          orientation === 'dashboard'
            ? intl.formatMessage({ id: 'reporting.my_first_dashboard', defaultMessage: 'My first Dashboard' })
            : intl.formatMessage({ id: 'reporting.my_first_report', defaultMessage: 'My first Report' });
      } else if (sourceTemplate?.name) {
        name = `${sourceTemplate.name.replace(`- ${workspaceTemplateLN}`, '')} - ${reportLN}`;
      }

      const highestURNbr = getHighestUntitledReportNum(reports, orientation, name.toLowerCase());
      return isOnboarding || sourceTemplate?.name
        ? `${name} ${highestURNbr + 1}`
        : orientation === 'dashboard'
        ? intl.formatMessage(
            { id: 'reporting.untitled_dashboard_number', defaultMessage: 'Untitled Dashboard {number}' },
            { number: highestURNbr + 1 }
          )
        : intl.formatMessage(
            { id: 'reporting.untitled_report_number', defaultMessage: 'Untitled Report {number}' },
            { number: highestURNbr + 1 }
          );
    },
    [reports, intl, getHighestUntitledReportNum]
  );

  /**
   * Attempts to retrieve ReportTemplate data for the given templateId
   */
  const getSourceTemplate = useCallback(
    async (templateId?: string, isConsolidation?: boolean) => {
      if (!templateId) {
        return undefined;
      }

      return await fetchReportTemplateById(workspaceId, organisationId, templateId, isConsolidation);
    },
    [organisationId, workspaceId]
  );

  /**
   * Makes a request to create a new report
   */
  const createNewReport = useCallback(
    async (orientation: OrientationType, templateId?: string, isOnboarding?: boolean, isConsolidation?: boolean) => {
      const sourceTemplate = (await getSourceTemplate(templateId, isConsolidation)) as ReportTemplateDetail;
      const newReportName = getNewReportName(orientation, sourceTemplate, isOnboarding);
      const pageSetup = {
        orientation,
        ...(sourceTemplate?.data as { pageSetup: Partial<PageSetup> })?.pageSetup
      } as PageSetup;

      const createdReport = await createReport(
        newReportName,
        pageSetup,
        templateId,
        sourceTemplate?.data_reference_mapping as ReportMappingData,
        hasSwitchLocaleEnabled ? intl.locale : undefined,
        sourceTemplate?.page_count
      );

      if (createdReport) {
        trackCreateReport(orientation, isConsolidation);
        return createdReport;
      }
    },
    [createReport, hasSwitchLocaleEnabled, getNewReportName, getSourceTemplate, intl.locale]
  );

  return {
    getNewReportName,
    getSourceTemplate,
    createNewReport
  };
};
